import React, { useEffect, useRef, useState } from 'react';
import toast, { type Toast } from 'react-hot-toast';

export const UNDO_TOAST_MS = 10_000;

interface UndoToastProps {
  toastInfo: Toast;
  message: string;
  undoLabel: string;
  onUndo: () => void;
  onExpire?: () => void;
}

export const UndoToast: React.FC<UndoToastProps> = ({ toastInfo, message, undoLabel, onUndo, onExpire }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const remaining = useRef(UNDO_TOAST_MS);
  const settled = useRef(false);
  const expire = useRef(onExpire);
  expire.current = onExpire;

  useEffect(() => {
    if (hovered || focused || !toastInfo.visible || settled.current) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      if (settled.current) return;
      settled.current = true;
      toast.dismiss(toastInfo.id);
      expire.current?.();
    }, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt));
    };
  }, [hovered, focused, toastInfo.visible, toastInfo.id]);

  return (
    <div
      role="status"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
      className={`flex items-center gap-4 rounded-xl border border-theme-modal-border bg-theme-modal px-4 py-3 text-[14px] text-theme-text-primary shadow-lg transition-opacity ${
        toastInfo.visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <span dir="auto">{message}</span>
      <button
        type="button"
        onClick={() => {
          settled.current = true;
          toast.dismiss(toastInfo.id);
          onUndo();
        }}
        className="shrink-0 font-semibold text-theme-accent-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-theme-accent-primary"
      >
        {undoLabel}
      </button>
    </div>
  );
};

export function showUndoToast(message: string, undoLabel: string, onUndo: () => void, onExpire?: () => void): string {
  return toast.custom(
    (toastInfo) => (
      <UndoToast toastInfo={toastInfo} message={message} undoLabel={undoLabel} onUndo={onUndo} onExpire={onExpire} />
    ),
    { duration: Infinity },
  );
}
